import { View, Text, Image, StyleSheet } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { useTranslation } from 'react-i18next'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import { LOGO } from '../../lib/assets'
import { raceLogo } from '../../lib/raceLogos'
import { RANK_COLORS } from '../../lib/rankColors'
import { textOn } from '../../lib/contrast'
import { darken, lighten } from '../personAvatarConfig'
import PersonAvatarFace from '../PersonAvatarFace'

// Immagine da condividere della classifica (verticale, pensata per storie e post). Disegno FISSO, uguale in tema chiaro e
// scuro e identico a quello delle immagini del Pick: fondo scuro con gradiente nel colore del brand.
// rows: [{ id_user, pos, name, value, tie }] gia' ordinate; si mostrano le prime 10 e, se sei oltre, la tua riga in fondo.
export const SHARE_W = 360
const TOP = 10

export default function ClassificaShareCard({ raceName, subtitle, chip, rows, currentUserId, leagueName, total }) {
  const { t, i18n } = useTranslation()
  const tc = useThemeColors()
  const logo = raceLogo(raceName)
  const deep = darken(tc.rosa, 0.3)
  const accent = lighten(tc.rosa, 0.45)           // accento: il brand, chiaro, leggibile sul fondo scuro
  const shown = rows.slice(0, TOP)
  const meIdx = currentUserId ? rows.findIndex(r => r.id_user === currentUserId) : -1
  const meExtra = meIdx >= TOP ? rows[meIdx] : null
  const avatars = useAvatars([...shown, meExtra].filter(Boolean).map(r => r.id_user))
  const dateLabel = new Date().toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })

  const renderRow = (r, i) => {
    const isMe = r.id_user === currentUserId
    const big = r.pos <= 3
    return (
      <View key={r.id_user} style={[s.row, big && s.rowBig, { backgroundColor: isMe ? tc.rosa + '55' : i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.09)' }]}>
        <Text style={[s.pos, big && { color: RANK_COLORS[r.pos], fontSize: 20 }]}>{r.pos}</Text>
        <PersonAvatarFace userId={r.id_user} settings={avatars[r.id_user]} size={big ? 34 : 24} ring={big ? RANK_COLORS[r.pos] : null} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={[s.name, big && { fontSize: 14 }]} numberOfLines={1}>{r.name}</Text>
          {r.tie && <Text style={s.tie}>{t('leaderboard.tie')}</Text>}
        </View>
        <Text style={[s.value, big && { fontSize: 15 }]}>{r.value}</Text>
      </View>
    )
  }

  return (
    <View style={{ width: SHARE_W, minHeight: (SHARE_W * 16) / 9, overflow: 'hidden', backgroundColor: '#0A0A10' }}>
      <LinearGradient colors={['#0A0A10', '#14080D', deep]} start={{ x: 0.1, y: 0 }} end={{ x: 0.9, y: 1 }} style={StyleSheet.absoluteFill} />
      <View style={{ position: 'absolute', top: -90, right: -90, width: 260, height: 260, borderRadius: 130, backgroundColor: tc.rosa + '26' }} />
      <View style={{ position: 'absolute', bottom: -130, left: -110, width: 320, height: 320, borderRadius: 160, backgroundColor: tc.rosa + '1A' }} />
      {logo && <Image source={logo} style={{ position: 'absolute', top: 30, right: -40, width: 220, height: 220, opacity: 0.12 }} resizeMode="contain" />}

      <View style={{ padding: 18 }}>
        {/* barra superiore */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Image source={LOGO} style={{ width: 28, height: 28 }} resizeMode="contain" />
            <Text style={{ color: '#fff', fontSize: 12, letterSpacing: 3, fontFamily: fonts.condensedBold }}>VIRTUA CYCLING</Text>
          </View>
          {!!leagueName && <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, letterSpacing: 2, fontFamily: fonts.condensed }} numberOfLines={1}>{leagueName.toUpperCase()}</Text>}
        </View>

        {/* titolo */}
        <View style={{ marginTop: 26 }}>
          <Text style={{ color: accent, fontSize: 11, letterSpacing: 5, fontFamily: fonts.condensedBold }}>{t('leaderboard.share_label')}</Text>
          <Text style={{ color: '#fff', fontSize: 36, lineHeight: 38, textTransform: 'uppercase', fontFamily: fonts.condensedBold, marginTop: 4 }} numberOfLines={3}>{raceName}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
            {chip && (
              <View style={{ backgroundColor: chip.color, borderRadius: 10, paddingHorizontal: 9, paddingVertical: 3 }}>
                <Text style={{ color: textOn(chip.color), fontSize: 10, letterSpacing: 1, fontFamily: fonts.condensedBold }}>{chip.label.toUpperCase()}</Text>
              </View>
            )}
            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, letterSpacing: 1.5, fontFamily: fonts.condensed, flexShrink: 1 }} numberOfLines={2}>
              {[subtitle, dateLabel, t('pick.scout_players', { count: total ?? rows.length })].filter(Boolean).join(' · ').toUpperCase()}
            </Text>
          </View>
        </View>

        {/* classifica */}
        <View style={s.table}>
          {shown.map(renderRow)}
          {meExtra && (
            <>
              <Text style={s.dots}>• • •</Text>
              {renderRow(meExtra, 1)}
            </>
          )}
        </View>

        {/* piede */}
        <View style={{ marginTop: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ color: tc.rosaLight ?? accent, fontSize: 14, letterSpacing: 1.5, fontFamily: fonts.condensedBold }}>#VIRTUACYCLING</Text>
          <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 9, letterSpacing: 2 }}>VIRTUACYCLING.COM</Text>
        </View>
      </View>
    </View>
  )
}

const s = StyleSheet.create({
  table: { marginTop: 18, borderRadius: 14, overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.25)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8, minHeight: 36 },
  rowBig: { minHeight: 48 },
  pos: { width: 22, textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: 13, fontFamily: fonts.condensedBold },
  name: { color: '#fff', fontSize: 12, textTransform: 'uppercase', fontFamily: fonts.condensedBold },
  tie: { color: 'rgba(255,255,255,0.55)', fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: fonts.condensedBold },
  value: { color: '#fff', fontSize: 12, fontFamily: fonts.condensedBold },
  dots: { color: 'rgba(255,255,255,0.4)', fontSize: 10, letterSpacing: 4, textAlign: 'center', paddingVertical: 4 },
})
